/**
 * O ecrã de amigos — a "casa" quando não se está em servidor nenhum.
 *
 * É a única vista que lê `relationships` do store: uma relação é sempre sobre
 * OUTRA pessoa (o par simétrico vive no servidor), por isso a chave do mapa é o
 * id do outro utilizador e daqui derivam-se as quatro listas — amigos, pedidos
 * recebidos, pedidos enviados, bloqueados — sem guardar nenhuma delas (estado
 * derivado guardado é estado que dessincroniza).
 *
 * As acções não esperam pelo eco do gateway: aplicam já o resultado com um
 * evento local (o `applyEvent` é idempotente por id), para o clique responder
 * na hora mesmo com a rede lenta.
 */

import { Limits, type PublicUser, type Relationship, type RelationshipKind } from "@greenvoice/shared";
import { useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../lib/api";
import { gatewayClient, useStore } from "../store/store";
import { AddFriendButton } from "./AddFriendButton";
import { Avatar, STATUS_LABEL } from "./Avatar";
import { IconHash } from "./Icons";
import { MenuButton } from "./MenuButton";

type FriendTab = "online" | "all" | "space" | "pending" | "blocked";

const TAB_LABEL: Record<FriendTab, string> = {
  online: "Online",
  all: "Todos",
  space: "Escritório",
  pending: "Pendentes",
  blocked: "Bloqueados",
};

/** Um amigo conta como "online" se está mesmo visível — invisível/offline não. */
function isActive(user: PublicUser): boolean {
  return user.status !== "OFFLINE" && user.status !== "INVISIBLE";
}

export function FriendsView() {
  const relationships = useStore((s) => s.relationships);
  const voiceStates = useStore((s) => s.voiceStates);
  const applyEvent = useStore((s) => s.applyEvent);

  const [tab, setTab] = useState<FriendTab>("online");

  // Uma passagem só sobre o mapa reparte as relações pelas quatro gavetas. O
  // `useMemo` acerta porque `relationships` só muda de referência quando muda
  // de conteúdo (o store devolve sempre um objecto novo nesse caso).
  const buckets = useMemo(() => {
    const friends: Relationship[] = [];
    const incoming: Relationship[] = [];
    const outgoing: Relationship[] = [];
    const blocked: Relationship[] = [];
    for (const relation of Object.values(relationships)) {
      switch (relation.kind) {
        case "FRIEND":
          friends.push(relation);
          break;
        case "INCOMING_REQUEST":
          incoming.push(relation);
          break;
        case "OUTGOING_REQUEST":
          outgoing.push(relation);
          break;
        case "BLOCKED":
          blocked.push(relation);
          break;
        // BLOCKED_BY não se mostra: quem me bloqueou não deve aparecer em lado nenhum.
      }
    }
    const byName = (a: Relationship, b: Relationship) =>
      (a.user.displayName ?? a.user.username).localeCompare(b.user.displayName ?? b.user.username);
    friends.sort(byName);
    blocked.sort(byName);
    // Pedidos por data, mais recente primeiro — é o que se quer ver primeiro.
    const byDate = (a: Relationship, b: Relationship) => b.createdAt.localeCompare(a.createdAt);
    incoming.sort(byDate);
    outgoing.sort(byDate);
    return { friends, incoming, outgoing, blocked };
  }, [relationships]);

  const online = buckets.friends.filter((r) => isActive(r.user));
  const pendingCount = buckets.incoming.length + buckets.outgoing.length;

  const rows: Relationship[] =
    tab === "online" ? online : tab === "all" ? buckets.friends : tab === "blocked" ? buckets.blocked : []; // "pending" tem o seu próprio layout (duas secções) mais abaixo.

  const tabs: FriendTab[] = ["online", "all", "space", "pending", "blocked"];

  return (
    <section className="chat friends">
      <header className="chat__head friends__head">
        <MenuButton />
        <IconHash size={18} />
        <h1>Amigos</h1>
        <span className="chat__divider" />
        <nav className="friends__tabs">
          {tabs.map((id) => (
            <button
              key={id}
              type="button"
              className={`friends__tab${tab === id ? " is-active" : ""}`}
              onClick={() => setTab(id)}
            >
              {TAB_LABEL[id]}
              {id === "pending" && pendingCount > 0 ? <span className="friends__tab-badge">{pendingCount}</span> : null}
              {id === "online" && online.length > 0 ? (
                <span className="friends__tab-count">{online.length}</span>
              ) : null}
            </button>
          ))}
        </nav>
      </header>

      <div className="friends__body">
        <AddFriend applyEvent={applyEvent} />

        {tab === "pending" ? (
          <PendingLists incoming={buckets.incoming} outgoing={buckets.outgoing} applyEvent={applyEvent} />
        ) : tab === "space" ? (
          <SpaceRoster />
        ) : (
          <div className="friends__list">
            <h4 className="friends__section">
              {tab === "online" ? "Online" : tab === "all" ? "Todos os amigos" : "Bloqueados"} — {rows.length}
            </h4>
            {rows.length === 0 ? (
              <p className="friends__empty">
                {tab === "blocked"
                  ? "Você não bloqueou ninguém."
                  : tab === "online"
                    ? "Nenhum amigo online agora."
                    : "Você ainda não tem amigos. Adicione alguém pelo nome de usuário."}
              </p>
            ) : (
              rows.map((relation) => (
                <FriendRow
                  key={relation.user.id}
                  relation={relation}
                  inVoice={Boolean(voiceStates[relation.user.id])}
                  applyEvent={applyEvent}
                />
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Quem está no escritório — toda a gente do servidor, com o botão de adicionar
 * já ao lado de cada nome.
 *
 * Existe porque adicionar alguém obrigava a SABER o nome de utilizador dele e a
 * escrevê-lo no formulário acima: quem se vê todos os dias no escritório 2D é
 * exactamente quem se quer adicionar, e era a única gente que não estava em
 * lado nenhum desta página.
 *
 * A fonte é `state.members` e NÃO `state.positions`. Parece o contrário: as
 * posições é que dizem quem está no mapa. Mas esta vista só existe quando não
 * há servidor activo (é o `@me`), e `positions` é indexado por servidor activo —
 * daqui está sempre vazio ou velho. `members` é a lista de quem pertence ao
 * escritório, que é a pergunta certa: quero poder adicionar o colega que hoje
 * não veio.
 *
 * A lista chega por pedido (`ready.service.ts` só manda o próprio); pede-se ao
 * abrir o separador, e o servidor responde com um `ServerMemberJoin` por pessoa.
 */
function SpaceRoster() {
  const servers = useStore((s) => s.servers);
  const members = useStore((s) => s.members);
  const relationships = useStore((s) => s.relationships);
  const selfId = useStore((s) => s.self?.id ?? null);
  const linked = useStore((s) => s.connectionStatus === "ready");

  // A dependência do efeito é a lista de ids COMO VALOR, e não o objecto
  // `servers`: esse muda de referência a cada `memberCount` que se mexe — e as
  // respostas a este próprio pedido mexem-lhe — portanto depender dele era
  // pedir os membros, receber, re-renderizar, e pedir outra vez para sempre.
  const serverIds = Object.keys(servers).sort().join(",");

  useEffect(() => {
    if (!linked) return;
    const client = gatewayClient();
    if (!client) return;
    for (const id of serverIds.split(",").filter(Boolean)) {
      client.requestMembers(id, undefined, Limits.membersPerFetch.max);
    }
  }, [linked, serverIds]);

  // Uma pessoa só aparece uma vez mesmo estando em dois escritórios: a chave de
  // `members` é `servidor:utilizador`, e o que interessa aqui é a PESSOA.
  const people = useMemo(() => {
    const seen = new Map<string, { user: PublicUser; name: string }>();
    for (const member of Object.values(members)) {
      const { user } = member;
      if (user.id === selfId || seen.has(user.id)) continue;
      // O nome como ele aparece no escritório: a alcunha do servidor ganha, que
      // é o nome por baixo do avatar no mapa 2D — se aqui aparecesse outro, a
      // lista deixava de ser reconhecível como "aquela gente".
      seen.set(user.id, { user, name: member.nickname ?? user.displayName ?? user.username });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [members, selfId]);

  const names = Object.values(servers).map((srv) => srv.name);
  const title = names.length === 1 ? names[0] : "No escritório";

  if (names.length === 0) {
    return <p className="friends__empty">Você ainda não está em nenhum escritório.</p>;
  }

  return (
    <div className="friends__list">
      <h4 className="friends__section">
        {title} — {people.length}
      </h4>
      {people.length === 0 ? (
        <p className="friends__empty">{linked ? "Carregando quem está no escritório…" : "Sem ligação ao servidor."}</p>
      ) : (
        people.map(({ user, name }) => (
          <div className="friends__row" key={user.id}>
            <Avatar user={user} size={40} showStatus />
            <div className="friends__ident">
              <span className="friends__name">{name}</span>
              {/* O handle e não o estado: o ponto do avatar já diz se está
                  online, e nesta lista — gente que ainda não conheço — o que
                  distingue dois "Guilherme" é o @nome, que é também o que a
                  API pede para os adicionar. */}
              <span className="friends__sub">@{user.username}</span>
            </div>
            <div className="friends__actions">
              {relationships[user.id]?.kind === "FRIEND" ? <span className="friends__tag">Amigo</span> : null}
              {/* Quando já é amigo (ou fui eu), este componente não desenha
                  nada — a regra vive lá dentro, uma vez só, partilhada com o
                  cartão de perfil. */}
              <AddFriendButton user={user} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** O formulário de adicionar por nome de utilizador. */
function AddFriend({ applyEvent }: { applyEvent: ReturnType<typeof useStore.getState>["applyEvent"] }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const username = name.trim();
    if (!username) return;
    setBusy(true);
    setOk(null);
    setError(null);
    try {
      const relation = await api.users.requestFriend(username);
      applyEvent({ type: "RelationshipUpdate", user: relation.user, kind: relation.kind });
      setName("");
      setOk(
        relation.kind === "FRIEND"
          ? `Já eram amigos — ${relation.user.displayName ?? relation.user.username} aceitou.`
          : `Pedido enviado a ${relation.user.displayName ?? relation.user.username}.`,
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 404
          ? "Não há ninguém com esse nome de usuário."
          : (caught as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="friends__add"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="friends__add-field">
        <span>Adicionar amigo</span>
        <div className="friends__add-row">
          <input
            value={name}
            maxLength={32}
            placeholder="Nome de usuário"
            onChange={(e) => {
              setName(e.target.value);
              setOk(null);
              setError(null);
            }}
          />
          <button type="submit" className="button button--primary" disabled={busy || !name.trim()}>
            {busy ? "Enviando…" : "Enviar pedido"}
          </button>
        </div>
      </label>
      {ok ? (
        <p className="friends__add-ok" role="status">
          {ok}
        </p>
      ) : null}
      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** As duas secções do separador "Pendentes": recebidos e enviados. */
function PendingLists({
  incoming,
  outgoing,
  applyEvent,
}: {
  incoming: Relationship[];
  outgoing: Relationship[];
  applyEvent: ReturnType<typeof useStore.getState>["applyEvent"];
}) {
  if (incoming.length === 0 && outgoing.length === 0) {
    return <p className="friends__empty">Sem pedidos pendentes.</p>;
  }
  return (
    <div className="friends__list">
      {incoming.length > 0 ? (
        <>
          <h4 className="friends__section">Recebidos — {incoming.length}</h4>
          {incoming.map((relation) => (
            <FriendRow key={relation.user.id} relation={relation} inVoice={false} applyEvent={applyEvent} />
          ))}
        </>
      ) : null}
      {outgoing.length > 0 ? (
        <>
          <h4 className="friends__section">Enviados — {outgoing.length}</h4>
          {outgoing.map((relation) => (
            <FriendRow key={relation.user.id} relation={relation} inVoice={false} applyEvent={applyEvent} />
          ))}
        </>
      ) : null}
    </div>
  );
}

/** Uma linha da lista: avatar, estado/atividade, e as acções conforme o tipo. */
function FriendRow({
  relation,
  inVoice,
  applyEvent,
}: {
  relation: Relationship;
  inVoice: boolean;
  applyEvent: ReturnType<typeof useStore.getState>["applyEvent"];
}) {
  const { user, kind } = relation;
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch {
      // A relação real fica pelo evento; um erro pontual não deve travar a UI.
    } finally {
      setBusy(false);
    }
  }

  const remove = () =>
    run(async () => {
      await api.users.removeFriend(user.id);
      applyEvent({ type: "RelationshipDelete", userId: user.id });
    });

  const accept = () =>
    run(async () => {
      const rel = await api.users.acceptFriend(user.id);
      applyEvent({ type: "RelationshipUpdate", user: rel.user, kind: rel.kind });
    });

  const block = () =>
    run(async () => {
      const rel = await api.users.block(user.id);
      applyEvent({ type: "RelationshipUpdate", user: rel.user, kind: rel.kind });
    });

  /**
   * Falar com a pessoa — o que se vem cá fazer.
   *
   * A lista tinha «Bloquear» e «Remover» e mais nada: as duas maneiras de
   * DESFAZER a amizade, e nenhuma de a usar. Quem quisesse escrever a um amigo
   * tinha de o ir procurar noutro sítio.
   *
   * É a mesma acção do cartão de perfil (`openDirectMessage`): abre a conversa
   * se já existir, cria-a se não. Uma regra num sítio só.
   */
  const mensagem = () =>
    run(async () => {
      await useStore.getState().openDirectMessage(user.id);
    });

  const subtitle = activityLine(relation, inVoice);

  return (
    <div className="friends__row">
      <Avatar user={user} size={40} showStatus />
      <div className="friends__ident">
        <span className="friends__name">{user.displayName ?? user.username}</span>
        <span className="friends__sub">{subtitle}</span>
      </div>
      <div className="friends__actions">{actionsFor(kind, { accept, remove, block, mensagem, busy })}</div>
    </div>
  );
}

/** A linha de baixo: atividade de voz tem prioridade sobre o estado/estado-texto. */
function activityLine(relation: Relationship, inVoice: boolean): string {
  const { user, kind } = relation;
  if (kind === "INCOMING_REQUEST") return "Quer ser seu amigo";
  if (kind === "OUTGOING_REQUEST") return "Pedido enviado";
  if (kind === "BLOCKED") return "Bloqueado";
  if (inVoice) return "Numa conversa de voz";
  if (user.statusText && isActive(user)) return user.statusText;
  return STATUS_LABEL[user.status];
}

function actionsFor(
  kind: RelationshipKind,
  h: { accept: () => void; remove: () => void; block: () => void; mensagem: () => void; busy: boolean },
) {
  switch (kind) {
    case "INCOMING_REQUEST":
      return (
        <>
          <button type="button" className="button button--small button--primary" disabled={h.busy} onClick={h.accept}>
            Aceitar
          </button>
          <button type="button" className="button button--small" disabled={h.busy} onClick={h.remove}>
            Recusar
          </button>
        </>
      );
    case "OUTGOING_REQUEST":
      return (
        <button type="button" className="button button--small" disabled={h.busy} onClick={h.remove}>
          Cancelar
        </button>
      );
    case "BLOCKED":
      return (
        <button type="button" className="button button--small" disabled={h.busy} onClick={h.remove}>
          Desbloquear
        </button>
      );
    case "FRIEND":
      return (
        <>
          {/* Primeiro, e destacado: numa linha onde as outras duas opções são
              «bloquear» e «remover», falar tem de ser a que salta à vista. */}
          <button type="button" className="button button--small button--primary" disabled={h.busy} onClick={h.mensagem}>
            Mensagem
          </button>
          <button type="button" className="button button--small" disabled={h.busy} onClick={h.block}>
            Bloquear
          </button>
          <button type="button" className="button button--small button--danger" disabled={h.busy} onClick={h.remove}>
            Remover
          </button>
        </>
      );
    default:
      return null;
  }
}
